import { Button } from '../button/Button';
import type { WeaponResponse } from '../../api/games';
import styles from './WeaponPoolCard.module.css';

type Props = {
  weapons: WeaponResponse[];
  isModerator: boolean;
  onApprove: (id: number) => void;
  onDecline: (id: number) => void;
  onRemove: (id: number) => void;
};

const STATUS_LABEL: Record<WeaponResponse['status'], string> = {
  approved: 'Ready',
  pending: 'Pending',
  declined: 'Declined',
};

export function WeaponPoolCard({ weapons, isModerator, onApprove, onDecline, onRemove }: Props) {
  const approved = weapons.filter((w) => w.status === 'approved');
  const pending = weapons.filter((w) => w.status === 'pending');
  const declined = weapons.filter((w) => w.status === 'declined');

  const ordered = [...approved, ...pending, ...declined];

  if (ordered.length === 0) {
    return (
      <div className={styles.pool}>
        <p className={styles.empty}>
          No weapons yet.{isModerator ? ' Browse templates or add a custom one.' : ''}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.pool}>
      <p className={styles.poolHeader}>
        Weapon pool · <span className={styles.count}>{approved.length} ready</span>
        {pending.length > 0 && (
          <span className={styles.pendingCount}> · {pending.length} pending</span>
        )}
      </p>
      <ul className={styles.list}>
        {ordered.map((w) => (
          <li key={w.id} className={`${styles.item} ${styles[w.status]}`}>
            <div className={styles.itemMain}>
              <span className={styles.name}>{w.name}</span>
              <span className={`${styles.badge} ${styles[`badge_${w.status}`]}`}>
                {STATUS_LABEL[w.status]}
              </span>
            </div>
            <p className={styles.description}>{w.description}</p>
            {w.suggested_by && <p className={styles.suggestedBy}>Suggested by {w.suggested_by}</p>}
            {isModerator && (
              <div className={styles.actions}>
                {w.status === 'pending' && (
                  <>
                    <Button onClick={() => onApprove(w.id)}>Approve</Button>
                    <Button variant="secondary" onClick={() => onDecline(w.id)}>
                      Decline
                    </Button>
                  </>
                )}
                <Button variant="danger" onClick={() => onRemove(w.id)}>
                  Remove
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
